import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { getUserFacingErrorMessage } from '@wlb/shared/utils/apiErrorHandler';

export type AsyncStatus = 'loading' | 'ready' | 'error';

export interface AsyncResource<T> {
  data: T | null;
  status: AsyncStatus;
  /**
   * True while a request runs *behind* data that is already on screen. Callers
   * pair this with the previous render instead of a spinner, which is what keeps
   * a search keystroke or a page change from looking like a reload.
   */
  refreshing: boolean;
  error: string | null;
  reload: () => void;
}

/**
 * Responses kept across unmounts, so returning from a detail screen to the list
 * paints the rows the operator left behind on the first frame and revalidates
 * behind them. Deliberately module-level and unbounded-in-lifetime-but-bounded-
 * in-size: the panel is a single-session tool, and a request/response cache tied
 * to a component would be defeated by the very navigation it exists for.
 */
const CACHE = new Map<string, unknown>();
const CACHE_LIMIT = 60;

const readCache = <T>(key: string | null): { value: T } | null => {
  if (key == null || !CACHE.has(key)) {
    return null;
  }
  return { value: CACHE.get(key) as T };
};

const writeCache = (key: string | null, value: unknown) => {
  if (key == null) {
    return;
  }
  // Re-insert so iteration order is least-recently-written first.
  CACHE.delete(key);
  CACHE.set(key, value);
  while (CACHE.size > CACHE_LIMIT) {
    const oldest = CACHE.keys().next();
    if (oldest.done) {
      break;
    }
    CACHE.delete(oldest.value);
  }
};

/**
 * Dropped on sign-out: the cache holds member records, and the next operator to
 * use the browser must not inherit them.
 */
export const clearAsyncResourceCache = () => {
  CACHE.clear();
};

/**
 * Loads a value and keeps it fresh.
 *
 * `requestKey` identifies the request; changing it refetches. When data for the
 * previous key is already rendered the hook keeps it and reports `refreshing`
 * instead of dropping back to `loading`, so a list never collapses to a spinner
 * between two populated states.
 *
 * `cacheNamespace` opts a resource into the cross-navigation cache above. Leave
 * it undefined for anything that must be read fresh every mount.
 */
export function useAsyncResource<T>(
  load: () => Promise<T>,
  requestKey: string,
  fallbackErrorMessage: string,
  enabled = true,
  cacheNamespace?: string,
): AsyncResource<T> {
  const cacheKey = cacheNamespace ? `${cacheNamespace}|${requestKey}` : null;
  const initial = readCache<T>(cacheKey);

  const [data, setData] = useState<T | null>(initial ? initial.value : null);
  const [status, setStatus] = useState<AsyncStatus>(initial ? 'ready' : 'loading');
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const loadRef = useRef(load);
  const fallbackRef = useRef(fallbackErrorMessage);
  const requestIdRef = useRef(0);
  // Read inside the effect only, so a changed key does not by itself refetch.
  const cacheKeyRef = useRef(cacheKey);
  const hasDataRef = useRef(initial != null);

  useLayoutEffect(() => {
    loadRef.current = load;
    fallbackRef.current = fallbackErrorMessage;
    cacheKeyRef.current = cacheKey;
  });

  const reload = useCallback(() => {
    setReloadToken(token => token + 1);
  }, []);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    const cached = readCache<T>(cacheKeyRef.current);
    if (cached) {
      setData(cached.value);
      setStatus('ready');
      hasDataRef.current = true;
    }

    if (hasDataRef.current) {
      // Something is already on screen — never take it away to show a spinner.
      setRefreshing(true);
    } else {
      setStatus('loading');
    }
    setError(null);

    Promise.resolve()
      .then(() => loadRef.current())
      .then(result => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        writeCache(cacheKeyRef.current, result);
        hasDataRef.current = true;
        setData(result);
        setStatus('ready');
        setRefreshing(false);
      })
      .catch((cause: unknown) => {
        if (requestIdRef.current !== requestId) {
          return;
        }
        setRefreshing(false);
        setError(getUserFacingErrorMessage(cause, fallbackRef.current));
        setStatus('error');
        // Rows that failed to refresh are stale, and a stale row shown without
        // any signal is worse than an empty panel. Clearing the flag also means
        // a retry shows the loading placeholder instead of leaving the error
        // panel on screen with no sign that anything is happening.
        hasDataRef.current = false;
      });
  }, [enabled, requestKey, reloadToken]);

  return { data, status, refreshing, error, reload };
}
